import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Clock,
  Building2,
  CheckCircle,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEB3FORMS_KEY = 'f69f0cf2-eba4-4b0f-99c7-6df04e3d665f';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c69a6c1b-5824-475e-9621-3a91f4bdc21c/artifacts/hu5ydhul_ChatGPT%20Image%20Jan%2016%2C%202026%2C%2011_17_05%20AM.png";

// Contact Info
const contactInfo = [
  {
    icon: Mail,
    title: 'Email',
    details: 'info@aisverify.com',
    link: 'mailto:info@aisverify.com',
  },
  {
    icon: Phone,
    title: 'Phone',
    details: '+91 7566602484, +91 9319559556',
    link: 'tel:7566602484',
  },
  {
    icon: MapPin,
    title: 'Address',
    details: 'Plot No. 39, 40, New Chouksey Nagar, Bhopal, Near MK Ponda College, 462038, Madhya Pradesh',
    link: null,
  },
  {
    icon: Clock,
    title: 'Business Hours',
    details: 'Monday - Saturday: 9:00 AM - 6:00 PM',
    link: null,
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    requirement: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.fullName || !formData.email || !formData.phone || !formData.requirement) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Store in database
      const response = await axios.post(`${API}/contact`, formData);
      
      // Try to send email via Web3Forms (client-side) - may fail in some environments
      try {
        const web3formData = {
          access_key: WEB3FORMS_KEY,
          subject: `New Contact Inquiry from ${formData.fullName}`,
          from_name: 'Aarambh Website',
          name: formData.fullName,
          email: formData.email,
          company: formData.companyName || 'Not provided',
          phone: formData.phone,
          requirement: formData.requirement,
        };

        await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(web3formData)
        });
      } catch (emailError) {
        console.log('Web3Forms email may have failed, but submission stored in database');
      }
      
      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted successfully!');
      setFormData({
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        requirement: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit inquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white py-20 lg:py-28" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Get in Touch
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              We would be glad to connect and understand your verification requirements 
              and explore how our services can add value to your organization.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="border-slate-100 shadow-soft">
                <CardContent className="p-8">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">Thank You!</h3>
                      <p className="text-slate-600 mb-6">
                        Your inquiry has been submitted successfully. Our team will get back to you within 24 hours.
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsSubmitted(false)}
                        data-testid="submit-another-btn"
                      >
                        Submit Another Inquiry
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="fullName">Full Name *</Label>
                            <Input
                              id="fullName"
                              name="fullName"
                              value={formData.fullName}
                              onChange={handleChange}
                              placeholder="John Doe"
                              required
                              className="h-12"
                              data-testid="input-full-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="companyName">Company Name</Label>
                            <Input
                              id="companyName"
                              name="companyName"
                              value={formData.companyName}
                              onChange={handleChange}
                              placeholder="Your Company"
                              className="h-12"
                              data-testid="input-company-name"
                            />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email Address *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="john@company.com"
                              required
                              className="h-12"
                              data-testid="input-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+91 9876543210"
                              required
                              className="h-12"
                              data-testid="input-phone"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="requirement">Requirement Description *</Label>
                          <Textarea
                            id="requirement"
                            name="requirement"
                            value={formData.requirement}
                            onChange={handleChange}
                            placeholder="Please describe your verification requirements..."
                            required
                            rows={5}
                            className="resize-none"
                            data-testid="input-requirement"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12"
                          disabled={isSubmitting}
                          data-testid="submit-inquiry-btn"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5 mr-2" />
                              Submit Inquiry
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Contact Info */}
            <div>
              <div className="mb-8">
                <img 
                  src={LOGO_URL} 
                  alt="Aarambh India Service" 
                  className="h-20 w-auto mb-6"
                />
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Aarambh India Service Pvt. Ltd.</h2>
                <p className="text-slate-600 leading-relaxed">
                  A trusted background verification and risk assessment organization, 
                  committed to delivering accurate, compliant, and timely verification 
                  solutions across India.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a 
                          href={info.link}
                          className="text-slate-600 hover:text-emerald-600 transition-colors"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-slate-600">{info.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="mt-8">
                <div className="bg-slate-100 rounded-xl h-64 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500">Bhopal, Madhya Pradesh</p>
                    <a 
                      href="https://maps.google.com/?q=New+Chouksey+Nagar+Bhopal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 text-sm font-medium hover:text-emerald-700 transition-colors"
                    >
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-slate-50" data-testid="additional-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Quick Response</h3>
              <p className="text-slate-600 text-sm">We respond to all inquiries within 24 hours</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Direct Support</h3>
              <p className="text-slate-600 text-sm">Dedicated account managers for all clients</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Pan-India Coverage</h3>
              <p className="text-slate-600 text-sm">Verification services across all states</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
